import { useEffect, useMemo, useRef, useState } from "react";
import { renderAsync as renderDocxAsync } from "docx-preview";
import {
  ArrowLeft,
  ArrowRight,
  Bot,
  ChevronRight,
  Download,
  Eye,
  FileText,
  Handshake,
  Search,
  ShieldCheck,
  Sparkles,
  WandSparkles,
  X,
} from "lucide-react";
import { fetchTemplateVersionFile, getTemplateOverridesWithDefaults, renderTemplatePdf, templateDownloadUrl } from "../lib/api";
import {
  classNames,
  cleanTemplateName,
  compactText,
  formatBytes,
  formatDate,
} from "../lib/format";
import { isYukuaiqianSignFileDirectSelection, openTemplateCall, resolvePublishedTemplateVersion } from "../lib/templateCall";
import { filterTemplatesByRegion } from "../lib/regionFilter";
import { hasCompanyScope, submodulesWithVisibleCounts, withTemplateSubmodule } from "../lib/templateVisibility";
import { buildTemplateVariableData, renderTemplateVariablesForDownload } from "../lib/templateVariableData";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  MetricStrip,
  PageHeader,
} from "./Shared";
import TemplateContentEditor from "./TemplateContentEditor";

// 模板列表统一分页，避免一次渲染大量文书导致界面卡顿。
const PAGE_SIZE = 20;
// 中文维护注释：按当前产品规则隐藏全部“获取原文件”入口；在线预览、模板调用和底层原文件接口继续保留。
const ORIGINAL_FILE_DOWNLOAD_VISIBLE = false;

function toRemoteTemplate(template, moduleData, fallbackSubmoduleKey = "") {
  const classifiedTemplate = withTemplateSubmodule(template, moduleData, fallbackSubmoduleKey);
  // 将企业模板转换成与系统模板一致的结构，供列表组件统一展示。
  return {
    ...classifiedTemplate,
    id: classifiedTemplate.id || "remote-" + Math.random(),
    fileName: classifiedTemplate.fileName || classifiedTemplate.templateName,
    displayName: classifiedTemplate.templateName || classifiedTemplate.fileName,
    previewText: classifiedTemplate.snippet || "",
    kind: classifiedTemplate.templateType || classifiedTemplate.kind || "system",
    isRemote: true,
  };
}

function moduleRegionalCount(module, companyRegion) {
  if (!hasCompanyScope(companyRegion)) return module.fileCount;
  if (!Array.isArray(module.files)) {
    return submodulesWithVisibleCounts(module, companyRegion)
      .reduce((sum, submodule) => sum + (submodule.visibleFileCount || 0), 0);
  }
  return filterTemplatesByRegion(module.files, companyRegion).length;
}

function moduleRegionalSubmoduleCount(module, companyRegion) {
  if (!hasCompanyScope(companyRegion)) return module.submoduleCount;
  return submodulesWithVisibleCounts(module, companyRegion).length;
}

function moduleRegionalDescription(module, companyRegion) {
  if (!hasCompanyScope(companyRegion)) return module.description;
  const visibleSubmodules = submodulesWithVisibleCounts(module, companyRegion);
  if (!visibleSubmodules.length) return module.description;
  return "当前可见目录：" + visibleSubmodules.map((submodule) => submodule.submoduleName).join("、") + "。";
}

function companyScopeLabel(companyRegion) {
  if (!hasCompanyScope(companyRegion)) return "真实文件索引";
  return [companyRegion?.fullName || "企业地区待完善", companyRegion?.industrySource].filter(Boolean).join(" / ") + "适用";
}

function moduleWithLoadedDetails(module, modulesByKey) {
  return modulesByKey?.[module.moduleKey] || module;
}

function TemplateOverview({ catalog, companyRegion, modulesByKey, modulesStatus, onOpenModule }) {
  const riskModules = catalog.modules
    .filter((module) => module.moduleKey !== "commercial-exchange")
    .map((module) => moduleWithLoadedDetails(module, modulesByKey));
  const commercialSummary = catalog.modules.find((module) => module.moduleKey === "commercial-exchange");
  const commercial = commercialSummary ? moduleWithLoadedDetails(commercialSummary, modulesByKey) : null;
  const regionalRiskModules = riskModules.map((module) => ({
    ...module,
    regionalFileCount: moduleRegionalCount(module, companyRegion),
    regionalSubmoduleCount: moduleRegionalSubmoduleCount(module, companyRegion),
    regionalDescription: moduleRegionalDescription(module, companyRegion),
  }));
  const regionalCommercial = commercial
    ? {
        ...commercial,
        regionalFileCount: moduleRegionalCount(commercial, companyRegion),
        regionalSubmoduleCount: moduleRegionalSubmoduleCount(commercial, companyRegion),
      }
    : null;
  const recentFiles = catalog.modules
    .flatMap((module) => module.featuredFiles || [])
    .filter((file) => filterTemplatesByRegion([file], companyRegion).length)
    .slice(0, 8);
  const scopedToCompany = hasCompanyScope(companyRegion);
  const hasLoadedModuleDetails = !scopedToCompany || modulesStatus === "ready";
  const regionalCatalogFileCount = scopedToCompany
    ? regionalRiskModules.reduce((sum, module) => sum + module.regionalFileCount, 0)
      + (regionalCommercial?.regionalFileCount || 0)
    : catalog.fileCount;

  return (
    <>
      <PageHeader
        eyebrow="文书模板"
        title="企业文书资产中心"
        description="统一调用系统模板、公司专用版本和个人常用文书，目录数据来自原用工风险管控系统。"
        meta={
          <>
            <span>最近同步 {formatDate(catalog.sourceGeneratedAt)}</span>
            <span>目录版本 {catalog.schemaVersion}</span>
            {scopedToCompany ? <span>当前范围 {companyScopeLabel(companyRegion).replace(/适用$/u, "")}</span> : null}
          </>
        }
      />

      <MetricStrip
        items={[
          { label: "系统模板", value: regionalCatalogFileCount.toLocaleString("zh-CN"), note: scopedToCompany ? (hasLoadedModuleDetails ? "当前企业可调用" : "正在按企业过滤") : companyScopeLabel(companyRegion) },
          { label: "风险模块", value: String(regionalRiskModules.length), note: "用工全周期" },
          { label: "商业文书", value: (regionalCommercial?.regionalFileCount || 0).toLocaleString("zh-CN"), note: "独立商业目录" },
          { label: "目录规模", value: formatBytes(catalog.totalSizeBytes), note: "原始文件总量" },
        ]}
      />

      <section className="workspace-section">
        <div className="section-title-row">
          <div>
            <h2>风险预防</h2>
            <p>按用工环节进入对应文书目录。</p>
          </div>
            <span>{regionalRiskModules.reduce((sum, module) => sum + module.regionalFileCount, 0).toLocaleString("zh-CN")} 份</span>
        </div>
        <div className="module-grid">
          {regionalRiskModules.map((module) => (
            <button
              className="module-card"
              key={module.moduleKey}
              type="button"
              onClick={() => onOpenModule(module.moduleKey, "risk")}
            >
              <div className="module-card-icon">
                <ShieldCheck size={18} aria-hidden="true" />
              </div>
              <div className="module-card-copy">
                <strong>{module.moduleName}</strong>
                <span>{compactText(module.regionalDescription, 38)}</span>
                <small>{module.regionalSubmoduleCount} 个目录 / {module.regionalFileCount} 份模板</small>
              </div>
              <ChevronRight size={17} aria-hidden="true" />
            </button>
          ))}
        </div>
      </section>

      {regionalCommercial ? (
        <section className="commercial-band">
          <div className="commercial-icon">
            <Handshake size={22} aria-hidden="true" />
          </div>
          <div>
            <h2>商业往来</h2>
            <p>{regionalCommercial.description}</p>
          </div>
          <div className="commercial-meta">
            <strong>{regionalCommercial.regionalFileCount.toLocaleString("zh-CN")}</strong>
            <span>份可调用模板</span>
          </div>
          <button
            className="secondary-button"
            type="button"
            onClick={() => onOpenModule("commercial-exchange", "commercial")}
          >
            打开目录
            <ArrowRight size={16} aria-hidden="true" />
          </button>
        </section>
      ) : null}

      <section className="workspace-section compact-section">
        <div className="section-title-row">
          <div>
            <h2>最近同步</h2>
            <p>来自原系统文件索引的模板记录。</p>
          </div>
        </div>
        <div className="recent-template-list">
          {recentFiles.map((file) => (
            <button
              key={file.moduleKey + file.id}
              type="button"
              onClick={() =>
                onOpenModule(
                  file.moduleKey,
                  file.moduleKey === "commercial-exchange" ? "commercial" : "risk",
                  file.submoduleKey,
                )
              }
            >
              <FileText size={17} aria-hidden="true" />
              <span>
                <strong>{cleanTemplateName(file.fileName)}</strong>
                <small>{file.moduleName} / {file.submoduleName}</small>
              </span>
              <time>{formatDate(file.modifiedAt)}</time>
            </button>
          ))}
        </div>
      </section>
    </>
  );
}

function TemplateRow({ file, selected, onPreview, onInvoke, directSelectionMode = false }) {
  const title = file.displayName || cleanTemplateName(file.fileName);
  const hasPreview = canPreviewTemplate(file);
  const sourceLabel = {
    system: "系统",
    company: "公司",
    personal: "个人",
  }[file.kind || "system"] || "系统";

  return (
    <article className={classNames("template-list-row", selected && "selected")}>
      <button className="template-row-main" type="button" onClick={() => (directSelectionMode ? onInvoke(file) : onPreview(file))}>
        <div className="file-type-icon">
          <FileText size={19} aria-hidden="true" />
          <span>{(file.extension || "DOC").toUpperCase()}</span>
        </div>
        <div className="template-row-copy">
          <div className="template-row-title">
            <h3>{title}</h3>
            <span className={classNames("source-badge", file.kind || "system")}>{sourceLabel}</span>
            {file.isCategoryDefault ? <span className="source-badge default">默认</span> : null}
          </div>
          <p>{compactText(file.previewText || file.snippet || "该模板已建立目录，可进入详情查看模板信息。", 112)}</p>
          <div className="template-row-meta">
            <span>{file.submoduleName || file.scenarioName || "通用目录"}</span>
            <span>{file.sizeBytes ? formatBytes(file.sizeBytes) : file.templateSource || "数据库模板"}</span>
            <span>{formatDate(file.modifiedAt || file.updatedAt)}</span>
            {file.previewError ? <span className="warning-text">解析需复核</span> : null}
          </div>
        </div>
      </button>
      <div className="template-row-actions">
        {!directSelectionMode ? (
          <button className="icon-text-button" type="button" onClick={() => onPreview(file)} disabled={!hasPreview}>
            <Eye size={16} aria-hidden="true" />
            预览
          </button>
        ) : null}
        <button className="primary-button compact" type="button" onClick={() => onInvoke(file)}>
          <WandSparkles size={16} aria-hidden="true" />
          调用
        </button>
      </div>
    </article>
  );
}

function templateFileExtension(file) {
  return String(file.extension || file.fileName?.split(".").pop() || "").toLowerCase();
}

function withOriginalFileExtension(name, extension) {
  const safeName = String(name || "").trim() || "template";
  if (!extension || /\.[A-Za-z0-9]{1,8}$/i.test(safeName)) return safeName;
  return `${safeName}.${extension}`;
}

function originalFileDownloadName(file) {
  return withOriginalFileExtension(
    file?.fileName || file?.displayName || cleanTemplateName(file?.fileName) || "template",
    templateFileExtension(file),
  );
}

function wordOriginalFileDownloadName(fileOrName) {
  const sourceName = typeof fileOrName === "string"
    ? fileOrName
    : fileOrName?.fileName || fileOrName?.displayName || cleanTemplateName(fileOrName?.fileName) || "template";
  const baseName = String(sourceName || "template").trim().replace(/\.[A-Za-z0-9]{1,8}$/i, "") || "template";
  return withOriginalFileExtension(baseName, "doc");
}

function escapedHtmlDownloadVariables(variables = {}) {
  return Object.fromEntries(
    Object.entries(variables).map(([key, value]) => [
      key,
      String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;"),
    ]),
  );
}

function buildOriginalDownloadVariables(company = {}, companyRegion = {}) {
  const normalizedCompany = company || {};
  const normalizedCompanyRegion = companyRegion || {};
  const variables = buildTemplateVariableData({ company: normalizedCompany });
  const regionName = normalizedCompanyRegion.fullName
    || normalizedCompanyRegion.shortName
    || normalizedCompany.companyRegion
    || normalizedCompany.region
    || "";
  const industryName = normalizedCompanyRegion.industrySource
    || normalizedCompany.companyIndustry
    || normalizedCompany.industry
    || "";
  if (regionName) variables.companyRegion = regionName;
  if (industryName) variables.companyIndustry = industryName;
  return variables;
}

function triggerBlobDownload(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function blankHtmlTemplateVariables(sourceHtml) {
  // 中文维护注释：获取原文件只下载空白文书模板，保留版式和字段标签，清空全部变量且不写入当前企业或员工数据。
  return renderTemplateVariablesForDownload(sourceHtml, {});
}

async function downloadReadableHtmlOriginal(file) {
  const response = await fetch(templateDownloadUrl(file.filePath), { cache: "no-store" });
  if (!response.ok) throw new Error("原文件读取失败");
  const sourceHtml = await response.text();
  const readableHtml = blankHtmlTemplateVariables(sourceHtml);
  triggerBlobDownload(
    new Blob([readableHtml], { type: "application/msword;charset=utf-8" }),
    wordOriginalFileDownloadName(file),
  );
}

function StaticOriginalFileLink({ file, className = "" }) {
  const [status, setStatus] = useState("idle");
  if (!ORIGINAL_FILE_DOWNLOAD_VISIBLE || !file?.filePath) return null;

  if (templateFileExtension(file) === "html") {
    async function downloadHtml() {
      setStatus("loading");
      try {
        await downloadReadableHtmlOriginal(file);
        setStatus("idle");
      } catch {
        setStatus("failed");
      }
    }

    return (
      <>
        <button
          className={classNames("secondary-button", className)}
          type="button"
          onClick={downloadHtml}
          disabled={status === "loading"}
        >
          <Download size={16} aria-hidden="true" />
          {status === "loading" ? "正在获取" : "获取原文件"}
        </button>
        {status === "failed" ? <span className="warning-text">原文件下载失败</span> : null}
      </>
    );
  }

  return (
    <a
      className={classNames("secondary-button", className)}
      href={templateDownloadUrl(file.filePath)}
      download={originalFileDownloadName(file)}
    >
      <Download size={16} aria-hidden="true" />
      获取原文件
    </a>
  );
}

function managedSourceFileName(file, version) {
  return version?.sourceFileName || originalFileDownloadName(file);
}

function isHtmlDownloadSource(fileName, blob) {
  return /\.html?$/i.test(String(fileName || "")) || String(blob?.type || "").includes("html");
}

async function readableManagedDownloadBlob(fileName, blob) {
  if (!isHtmlDownloadSource(fileName, blob)) return blob;
  const sourceHtml = await blob.text();
  const readableHtml = blankHtmlTemplateVariables(sourceHtml);
  return new Blob([readableHtml], { type: "application/msword;charset=utf-8" });
}

function ManagedOriginalDownloadButton({ file, version }) {
  const [status, setStatus] = useState("idle");
  if (!ORIGINAL_FILE_DOWNLOAD_VISIBLE || !version?.id) return null;

  async function downloadSource() {
    setStatus("loading");
    try {
      const sourceName = managedSourceFileName(file, version);
      const sourceBlob = await fetchTemplateVersionFile(version.id, "source");
      const downloadBlob = await readableManagedDownloadBlob(sourceName, sourceBlob);
      const sourceDownloadName = isHtmlDownloadSource(sourceName, sourceBlob) ? wordOriginalFileDownloadName(sourceName) : sourceName;
      triggerBlobDownload(downloadBlob, sourceDownloadName);
      setStatus("idle");
    } catch {
      setStatus("failed");
    }
  }

  return (
    <div className="document-preview-actions">
      <button className="secondary-button" type="button" onClick={downloadSource} disabled={status === "loading"}>
        <Download size={16} aria-hidden="true" />
        {status === "loading" ? "正在获取" : "获取原文件"}
      </button>
      {status === "failed" ? <span className="warning-text">原文件下载失败</span> : null}
    </div>
  );
}

function canPreviewOriginalFile(file) {
  return Boolean(file.filePath && ["doc", "docx", "pdf"].includes(templateFileExtension(file)));
}

function canPreviewHtmlSourceFile(file) {
  return Boolean(file.filePath && templateFileExtension(file) === "html");
}

function canPreviewManagedTemplate(file) {
  return Boolean(
    file.managedTemplateId
    || file.publishedVersion?.renderedPdfUrl
    || file.publishedVersion?.editableHtml
    || file.publishedVersion?.templateHtml
    || file.publishedVersion?.documentJson,
  );
}

function canPreviewTemplate(file) {
  return canPreviewOriginalFile(file) || canPreviewHtmlSourceFile(file) || canPreviewManagedTemplate(file);
}

function pdfPreviewUrl(pdfUrl) {
  if (!pdfUrl) return "";
  return pdfUrl + "#toolbar=1&navpanes=0&zoom=page-width";
}

function canPreviewPublishedVersionInEditor(version) {
  return Boolean(version && version.editorFormat !== "fixed" && (version.documentJson || version.editableHtml));
}

function HtmlOriginalPreview({ file, title, downloadVariables = {} }) {
  const [status, setStatus] = useState("loading");
  const [sourceHtml, setSourceHtml] = useState("");
  const downloadUrl = templateDownloadUrl(file.filePath);
  const previewVariablesJson = JSON.stringify(escapedHtmlDownloadVariables(downloadVariables));

  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");
    setSourceHtml("");

    fetch(downloadUrl, { cache: "no-store", signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error("HTML 原文件读取失败");
        return response.text();
      })
      .then((html) => {
        if (controller.signal.aborted) return;
        // 中文维护注释：静态 HTML 系统模板预览读取真实源文件并套用当前变量；沙箱 iframe 禁止模板脚本执行。
        setSourceHtml(renderTemplateVariablesForDownload(
          html,
          JSON.parse(previewVariablesJson),
        ));
        setStatus("ready");
      })
      .catch((error) => {
        if (error.name !== "AbortError") setStatus("failed");
      });

    return () => controller.abort();
  }, [downloadUrl, previewVariablesJson]);

  return (
    <div className="document-html-preview" aria-label={title + " HTML原文件预览"}>
      {status === "loading" ? <div className="document-html-status">正在读取 HTML 原文件...</div> : null}
      {status === "failed" ? (
        <div className="document-file-state">
          <FileText size={30} aria-hidden="true" />
          <strong>HTML 原文件读取失败</strong>
          <p>请稍后重试或联系管理员。</p>
        </div>
      ) : null}
      {status === "ready" ? (
        <iframe
          className="document-html-frame"
          title={title + " HTML原文件预览"}
          sandbox=""
          referrerPolicy="no-referrer"
          srcDoc={sourceHtml}
        />
      ) : null}
    </div>
  );
}

function DocxBrowserPreview({ file, title, downloadVariables = {} }) {
  const hostRef = useRef(null);
  const [status, setStatus] = useState("loading");
  const downloadUrl = templateDownloadUrl(file.filePath);

  useEffect(() => {
    const controller = new AbortController();
    const host = hostRef.current;
    setStatus("loading");
    if (host) host.replaceChildren();

    fetch(downloadUrl, { cache: "no-store", signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error("原文件读取失败");
        return response.arrayBuffer();
      })
      .then((buffer) => {
        if (!host || controller.signal.aborted) return;
        // 中文维护注释：PDF 转换不可用时直接渲染原 DOCX 结构，禁止退化为目录抽文本假预览。
        return renderDocxAsync(buffer, host, undefined, {
          className: "docx-original-page",
          inWrapper: true,
          breakPages: true,
          ignoreLastRenderedPageBreak: false,
        });
      })
      .then(() => {
        if (!controller.signal.aborted) setStatus("ready");
      })
      .catch((error) => {
        if (error.name !== "AbortError") setStatus("failed");
      });

    return () => controller.abort();
  }, [downloadUrl]);

  return (
    <div className="document-docx-preview" aria-label={title + " DOCX原文件预览"}>
      {status === "loading" ? <div className="document-docx-status">正在读取 DOCX 原文件...</div> : null}
      {status === "failed" ? (
        <div className="document-file-state">
          <FileText size={30} aria-hidden="true" />
          <strong>原文件读取失败</strong>
          <StaticOriginalFileLink file={file} />
        </div>
      ) : null}
      <div ref={hostRef} hidden={status === "failed"} />
    </div>
  );
}

function OriginalFilePreview({ file, title, downloadVariables = {} }) {
  const [status, setStatus] = useState("loading");
  const [renderResult, setRenderResult] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");
    setRenderResult(null);
    renderTemplatePdf(file, {}, controller.signal)
      .then((payload) => {
        setRenderResult(payload);
        setStatus(payload.pdfUrl ? "ready" : "docx-fallback");
      })
      .catch((error) => {
        if (error.name !== "AbortError") setStatus("docx-fallback");
      });
    return () => controller.abort();
  }, [file]);

  if (status === "ready" && renderResult?.pdfUrl) {
    return (
      <div className="document-file-preview">
        <iframe className="document-frame" title={title + " 文件预览"} src={pdfPreviewUrl(renderResult.pdfUrl)} />
      </div>
    );
  }

  if (status === "docx-fallback" && templateFileExtension(file) === "docx") {
    return <DocxBrowserPreview file={file} title={title} downloadVariables={downloadVariables} />;
  }

  return (
    <div className="document-file-state">
      <FileText size={30} aria-hidden="true" />
      <strong>{status === "loading" ? "正在生成原文件 PDF 预览" : "原文件渲染失败"}</strong>
      <p>{status === "loading" ? "首次打开 DOCX 模板需要一点转换时间。" : "请稍后重试或联系管理员，系统不会降级显示抽文本结果。"}</p>
      {status !== "loading" ? <StaticOriginalFileLink file={file} /> : null}
    </div>
  );
}

function PublishedVersionPreview({ file, title, downloadVariables = {} }) {
  const [status, setStatus] = useState("loading");
  const [version, setVersion] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    setStatus("loading");
    setVersion(null);
    resolvePublishedTemplateVersion(file, controller.signal)
      .then((publishedVersion) => {
        setVersion(publishedVersion);
        setStatus(publishedVersion?.renderedPdfUrl || canPreviewPublishedVersionInEditor(publishedVersion) ? "ready" : "empty");
      })
      .catch((error) => {
        if (error.name !== "AbortError") setStatus("failed");
      });
    return () => controller.abort();
  }, [file]);

  if (status === "ready" && canPreviewPublishedVersionInEditor(version)) {
    return (
      <div className="document-editor-preview">
        <ManagedOriginalDownloadButton file={file} version={version} />
        <TemplateContentEditor
          version={version}
          readOnly
          preview
          onDirtyChange={() => {}}
        />
      </div>
    );
  }

  if (status === "ready" && version?.renderedPdfUrl) {
    return (
      <div className="document-file-preview">
        <ManagedOriginalDownloadButton file={file} version={version} />
        <iframe className="document-frame" title={title + " 发布版本预览"} src={pdfPreviewUrl(version.renderedPdfUrl)} />
      </div>
    );
  }

  return (
    <div className="document-file-state">
      <FileText size={30} aria-hidden="true" />
      <strong>{status === "loading" ? "正在读取已发布模板" : status === "empty" ? "暂无已发布模板" : "发布模板读取失败"}</strong>
      <p>{status === "loading" ? "系统正在从统一模板资产库读取当前默认版本。" : "请到平台端确认该模板已发布并保持默认配置。"}</p>
    </div>
  );
}

function TemplatePreviewDialog({ file, company, companyRegion, onClose, onInvoke, onAskAi }) {
  if (!file) return null;
  const title = file.displayName || cleanTemplateName(file.fileName);
  const downloadVariables = buildOriginalDownloadVariables(company, companyRegion);
  const showManagedPreview = canPreviewManagedTemplate(file);
  const showOriginalPreview = !showManagedPreview && canPreviewOriginalFile(file);
  const showHtmlSourcePreview = !showManagedPreview && canPreviewHtmlSourceFile(file);
  const useManagedPreviewShell = showManagedPreview;
  // 中文维护注释：渝快签系统模版直选模式只负责选择文书，真正回填由列表调用分支发消息给父页面。
  const directInvocationMode = isYukuaiqianSignFileDirectSelection();

  return (
    <div className="dialog-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className={classNames(
          "template-dialog",
          useManagedPreviewShell && "has-managed-preview",
          directInvocationMode && "is-direct-invocation",
        )}
        role="dialog"
        aria-modal="true"
        aria-labelledby="template-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="dialog-header">
          <div>
            <span>{file.moduleName || "文书模板"} / {file.submoduleName || file.scenarioName || "通用"}</span>
            <h2 id="template-dialog-title">{title}</h2>
          </div>
          <button className="icon-button" type="button" onClick={onClose} aria-label="关闭预览">
            <X size={19} />
          </button>
        </header>

        <div className="dialog-body">
          {!directInvocationMode ? <aside className="template-metadata">
            <dl>
              <div>
                <dt>来源</dt>
                <dd>{file.isRemote ? file.templateSource || "数据库模板" : "系统文件库"}</dd>
              </div>
              <div>
                <dt>格式</dt>
                <dd>{(file.extension || "HTML").toUpperCase()}</dd>
              </div>
              <div>
                <dt>大小</dt>
                <dd>{file.sizeBytes ? formatBytes(file.sizeBytes) : "动态模板"}</dd>
              </div>
              <div>
                <dt>更新时间</dt>
                <dd>{formatDate(file.modifiedAt || file.updatedAt)}</dd>
              </div>
            </dl>
            <StaticOriginalFileLink file={file} className="full-width" />
            {onAskAi ? (
              <button
                className="secondary-button full-width"
                type="button"
                onClick={() => onAskAi("请审查当前模板“" + title + "”，指出适用场景、必备变量和主要风险。")}
              >
                <Bot size={16} aria-hidden="true" />
                交给 AI 审查
              </button>
            ) : null}
          </aside> : null}

          <div className="document-preview">
            {showOriginalPreview ? (
              <OriginalFilePreview file={file} title={title} downloadVariables={downloadVariables} />
            ) : showHtmlSourcePreview ? (
              <HtmlOriginalPreview file={file} title={title} downloadVariables={downloadVariables} />
            ) : showManagedPreview ? (
              <PublishedVersionPreview file={file} title={title} downloadVariables={downloadVariables} />
            ) : (
              <div className="document-file-state">
                <FileText size={30} aria-hidden="true" />
                <strong>无法生成原文件预览</strong>
                <p>该记录没有可读取的原文件，请检查模板资产是否完整。</p>
                {directInvocationMode ? <StaticOriginalFileLink file={file} /> : null}
              </div>
            )}
          </div>
        </div>

        <footer className="dialog-footer">
          <button className="secondary-button" type="button" onClick={onClose}>关闭</button>
          <button className="primary-button" type="button" onClick={() => onInvoke(file)}>
            <WandSparkles size={16} aria-hidden="true" />
            {directInvocationMode ? "调用" : "调用此模板"}
          </button>
        </footer>
      </section>
    </div>
  );
}

function TemplateLibrary({
  moduleData,
  moduleStatus,
  domain,
  selectedModuleKey,
  selectedSubmoduleKey,
  onSubmoduleChange,
  company,
  companyRegion,
  onAskAi,
  onInvoke,
  onContextChange,
}) {
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState("all");
  const [page, setPage] = useState(1);
  const [previewFile, setPreviewFile] = useState(null);
  const [invokeError, setInvokeError] = useState("");
  const [overrideData, setOverrideData] = useState(null);
  const [overrideStatus, setOverrideStatus] = useState("idle");
  const directSelectionMode = isYukuaiqianSignFileDirectSelection();
  const moduleMatchesSelection = Boolean(
    moduleData && moduleData.moduleKey === selectedModuleKey,
  );

  useEffect(() => {
    setPage(1);
    setPreviewFile(null);
  }, [moduleData?.moduleKey, selectedSubmoduleKey, query, scope, company?.id, company?.name,
    companyRegion?.fullName, companyRegion?.industrySource, companyRegion?.scopeRequired]);

  useEffect(() => {
    if (!moduleData) {
      setOverrideData(null);
      setOverrideStatus("idle");
      return undefined;
    }

    const controller = new AbortController();
    setOverrideData(null);
    setOverrideStatus("loading");

    getTemplateOverridesWithDefaults(
      {
        moduleKey: moduleData.moduleKey,
        moduleName: moduleData.moduleName,
        companyId: company?.id,
        companyName: company?.name,
      },
      controller.signal,
    )
      .then((payload) => {
        setOverrideData(payload);
        setOverrideStatus("ready");
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        setOverrideData(null);
        setOverrideStatus("error");
      });

    return () => controller.abort();
  }, [company, moduleData]);

  const remoteFiles = useMemo(
    () => (overrideData?.templates || []).map((template) => toRemoteTemplate(template, moduleData)),
    [moduleData, overrideData],
  );

  const visibleFiles = useMemo(() => {
    if (!moduleData) return [];
    const normalizedQuery = query.trim().toLowerCase();
    const localFiles = moduleData.files
      .filter(
        (file) =>
          selectedSubmoduleKey === "all" || file.submoduleKey === selectedSubmoduleKey,
      )
      .map((file) => ({ ...file, kind: "system" }));
    const selectedRemoteFiles = remoteFiles.filter(
      (file) => selectedSubmoduleKey === "all" || file.submoduleKey === selectedSubmoduleKey,
    );
    const sourceFiles =
      scope === "system"
        ? localFiles.concat(selectedRemoteFiles.filter((file) => file.kind === "system"))
        : scope === "company"
          ? selectedRemoteFiles.filter((file) => file.kind === "company")
          : scope === "personal"
            ? selectedRemoteFiles.filter((file) => file.kind === "personal")
            : localFiles.concat(selectedRemoteFiles);
    const regionalFiles = filterTemplatesByRegion(sourceFiles, companyRegion);

    return regionalFiles.filter((file) => {
      if (!normalizedQuery) return true;
      return [
        file.fileName,
        file.displayName,
        file.submoduleName,
        file.scenarioName,
        file.templateSource,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedQuery);
    }).sort((left, right) => Number(Boolean(right.isCategoryDefault)) - Number(Boolean(left.isCategoryDefault)));
  }, [companyRegion, moduleData, query, remoteFiles, scope, selectedSubmoduleKey]);
  const visibleSubmodules = useMemo(
    () => (moduleMatchesSelection
      ? submodulesWithVisibleCounts(moduleData, companyRegion, remoteFiles)
      : []),
    [companyRegion, moduleData, moduleMatchesSelection, remoteFiles],
  );

  async function invokeTemplate(file) {
    setInvokeError("");
    try {
      await onInvoke(file);
    } catch (error) {
      setInvokeError(error.message || "模板调用失败，请稍后重试");
    }
  }

  useEffect(() => {
    if (!moduleMatchesSelection) return;
    if (selectedSubmoduleKey === "all") return;
    if (visibleSubmodules.some((submodule) => submodule.submoduleKey === selectedSubmoduleKey)) return;
    onSubmoduleChange("all");
  }, [moduleMatchesSelection, onSubmoduleChange, selectedSubmoduleKey, visibleSubmodules]);

  if (moduleStatus === "loading" || !moduleMatchesSelection) {
    return <LoadingState label="正在读取模块模板" />;
  }

  const selectedSubmodule = visibleSubmodules.find(
    (item) => item.submoduleKey === selectedSubmoduleKey,
  );
  const visibleModuleDescription = moduleRegionalDescription(moduleData, companyRegion);
  const pageCount = Math.max(1, Math.ceil(visibleFiles.length / PAGE_SIZE));
  const pageFiles = visibleFiles.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <PageHeader
        eyebrow={domain === "commercial" ? "商业往来" : "风险预防"}
        title={moduleData.moduleName}
        description={visibleModuleDescription}
        meta={
          <>
            <span>{moduleData.moduleDirectory}</span>
            <span>{moduleData.fileCount.toLocaleString("zh-CN")} 份原始系统模板</span>
            {hasCompanyScope(companyRegion) ? <span>{companyScopeLabel(companyRegion)}</span> : null}
          </>
        }
      />

      <MetricStrip
        items={[
          { label: "原始文件", value: moduleData.fileCount.toLocaleString("zh-CN"), note: "过滤前总量" },
          { label: "业务目录", value: String(visibleSubmodules.length), note: "可按场景筛选" },
          { label: "当前结果", value: visibleFiles.length.toLocaleString("zh-CN"), note: hasCompanyScope(companyRegion) ? companyScopeLabel(companyRegion) : (selectedSubmodule?.submoduleName || "全部目录") },
          { label: "文件规模", value: formatBytes(moduleData.totalSizeBytes), note: "模块合计" },
        ]}
      />

      <section className="library-panel">
        <div className="library-toolbar">
          <label className="select-field">
            <span>业务目录</span>
            <select
              value={selectedSubmoduleKey}
              onChange={(event) => onSubmoduleChange(event.target.value)}
            >
              <option value="all">全部目录</option>
              {visibleSubmodules.map((submodule) => (
                <option value={submodule.submoduleKey} key={submodule.submoduleKey}>
                  {submodule.submoduleName}（{submodule.visibleFileCount ?? submodule.fileCount}）
                </option>
              ))}
            </select>
          </label>
          <label className="search-input">
            <Search size={17} aria-hidden="true" />
            <span className="sr-only">搜索模板</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="搜索模板名称或目录"
            />
            {query ? (
              <button type="button" onClick={() => setQuery("")} aria-label="清空搜索">
                <X size={15} />
              </button>
            ) : null}
          </label>
          <div className="scope-switcher" aria-label="模板来源">
            {[
              ["all", "全部"],
              ["system", "系统"],
              ["company", "公司"],
              ["personal", "个人"],
            ].map(([value, label]) => (
              <button
                className={scope === value ? "active" : ""}
                key={value}
                type="button"
                onClick={() => setScope(value)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {selectedSubmoduleKey === "all" && ["company", "personal"].includes(scope) ? (
          <EmptyState
            title="请先选择一个业务目录"
            description="公司专用和个人默认模板按具体业务场景从原系统数据库读取。"
          />
        ) : overrideStatus === "loading" && ["company", "personal"].includes(scope) ? (
          <LoadingState label="正在读取专用模板" />
        ) : pageFiles.length ? (
          <div className="template-list">
            {pageFiles.map((file) => (
              <TemplateRow
                file={file}
                key={(file.kind || "system") + file.id}
                selected={previewFile?.id === file.id}
                onPreview={(selectedFile) => {
                  setPreviewFile(selectedFile);
                  onContextChange({
                    page: "模板预览",
                    currentProject: selectedFile.submoduleName || selectedFile.scenarioName || "",
                    templateName: selectedFile.displayName || cleanTemplateName(selectedFile.fileName),
                    templateId: selectedFile.id,
                  });
                }}
                onInvoke={invokeTemplate}
                directSelectionMode={directSelectionMode}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            title="没有匹配的模板"
            description={
              scope === "company" || scope === "personal"
                ? "当前企业尚未设置该层级的专用模板。"
                : "调整搜索词或切换业务目录后再试。"
            }
          />
        )}

        {pageCount > 1 ? (
          <div className="pagination">
            <button
              className="icon-button"
              type="button"
              onClick={() => setPage((value) => Math.max(1, value - 1))}
              disabled={page === 1}
              aria-label="上一页"
            >
              <ArrowLeft size={17} />
            </button>
            <span>第 {page} / {pageCount} 页，共 {visibleFiles.length} 份</span>
            <button
              className="icon-button"
              type="button"
              onClick={() => setPage((value) => Math.min(pageCount, value + 1))}
              disabled={page === pageCount}
              aria-label="下一页"
            >
              <ArrowRight size={17} />
            </button>
          </div>
        ) : null}
      </section>

      {invokeError ? <div className="workbench-message is-error" role="alert">{invokeError}</div> : null}

      <TemplatePreviewDialog
        file={previewFile}
        company={company}
        companyRegion={companyRegion}
        onClose={() => setPreviewFile(null)}
        onInvoke={(file) => {
          setPreviewFile(null);
          invokeTemplate(file);
        }}
        onAskAi={onAskAi}
      />
    </>
  );
}

export default function ClientWorkspace({
  catalog,
  catalogStatus,
  catalogError,
  onCatalogRetry,
  domain,
  onDomainChange,
  selectedModuleKey,
  onModuleChange,
  selectedSubmoduleKey,
  onSubmoduleChange,
  moduleData,
  moduleStatus,
  overviewModulesByKey = {},
  overviewModulesStatus = "idle",
  company,
  companyRegion,
  onAskAi,
  onContextChange,
}) {
  useEffect(() => {
    const moduleName =
      catalog?.modules.find((module) => module.moduleKey === selectedModuleKey)?.moduleName || "";
    onContextChange({
      page: domain === "templates" ? "文书模板" : moduleName,
      currentProject:
        moduleData?.submodules.find((item) => item.submoduleKey === selectedSubmoduleKey)
          ?.submoduleName || "",
      templateName: "",
      templateId: "",
    });
  }, [
    catalog,
    domain,
    moduleData,
    onContextChange,
    selectedModuleKey,
    selectedSubmoduleKey,
  ]);

  if (catalogStatus === "loading") {
    return <LoadingState label="正在同步文书资产" />;
  }

  if (catalogStatus === "error" || !catalog) {
    return <ErrorState message={catalogError} onRetry={onCatalogRetry} />;
  }

  if (domain === "templates") {
    return (
      <TemplateOverview
        catalog={catalog}
        companyRegion={companyRegion}
        modulesByKey={overviewModulesByKey}
        modulesStatus={overviewModulesStatus}
        onOpenModule={(moduleKey, nextDomain, submoduleKey) => {
          onModuleChange(moduleKey, nextDomain);
          if (submoduleKey) onSubmoduleChange(submoduleKey);
          onDomainChange(nextDomain);
        }}
      />
    );
  }

  return (
    <TemplateLibrary
      moduleData={moduleData}
      moduleStatus={moduleStatus}
      domain={domain}
      selectedModuleKey={selectedModuleKey}
      selectedSubmoduleKey={selectedSubmoduleKey}
      onSubmoduleChange={onSubmoduleChange}
      company={company}
      companyRegion={companyRegion}
      onAskAi={onAskAi}
      onInvoke={(file) => openTemplateCall(file, company, companyRegion)}
      onContextChange={onContextChange}
    />
  );
}
